import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { SnAbilityService } from '@lib/ability';
import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import {
  SnLayer,
  SnLayerType,
  SnInternalLayer,
  SnExternalLayer,
  SnEsriQueryLayer,
  SnEsriStreamLayer,
  SnGeoJSONLayer,
  SnInternalGeoJSONLayer,
  SnLayerHit,
  SnIntegrationLayer,
  SnFirstTwoLayer,
  SnRedbookLayer,
  SnAerisWeatherLayer,
  SnShare,
} from '@lib/api/entities';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnLayersAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _dialogService: SnDialogService,
    private readonly _ability: SnAbilityService,
  ) {}

  public getLayerList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnLayer>> {
    return this._httpClient.get('/api/layers/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.filter(this.supportedLayers.bind(this)).map(this.toLayerModel),
        ordering: response.ordering,
      })),
    );
  }

  public getIntegrations(params?: ISnListQueryParameters): Observable<ISnListResponse<SnLayer>> {
    params.type = 'Integration';
    return this._httpClient.get('/api/layers/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.filter(this.supportedLayers).map(this.toLayerModel),
        ordering: response.ordering,
      })),
    );
  }

  public getLayer(id: string): Observable<SnLayer> {
    return this._httpClient.get(`/api/layers/${id}/`).pipe(map(this.toLayerModel.bind(this)));
  }

  public createLayer(data: any): Observable<SnLayer> {
    return this._httpClient.post('/api/layers/', data).pipe(map(this.toLayerModel.bind(this)));
  }

  public updateLayer(id: string, data: any): Observable<SnLayer> {
    return this._httpClient
      .patch(`/api/layers/${id}/`, data)
      .pipe(map(this.toLayerModel.bind(this)));
  }

  public deleteLayer(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'Are you sure you want to delete the layer?',
        confirmButton: {
          text: 'DELETE',
          color: 'warn',
        },
      })
      .pipe(switchMap(() => this._httpClient.delete(`/api/layers/${id}/`)));
  }

  public getLayerHits(
    id: string,
    params?: ISnListQueryParameters,
    organization?: string,
    layer?: SnLayer,
  ): Observable<ISnListResponse<SnLayerHit>> {
    return this._httpClient.get(`/api/layers/${id}/hits/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map(
          (hit) =>
            new SnLayerHit({
              ...hit,
              layer: id,
              layerName: layer?.name,
              organization,
            }),
        ),
        facetFields: response.facetFields,
      })),
    );
  }

  public getLayerObjects(id: string, params?: ISnListQueryParameters): Observable<any> {
    return this._httpClient.get(`/api/layers/${id}/objects/`, { params });
  }

  public getCadHeatmap(id: string, params?: any): Observable<any> {
    return this._httpClient.get(`/api/layers/${id}/hits/heatmap/`, { params }).pipe(
      map((response: any) => {
        return response.heatmap.map((result) => {
          return {
            location: result.location,
            count: result.count,
          };
        });
      }),
    );
  }

  public execute(id: string, data: any): Observable<any> {
    return this._httpClient.post(`/api/layers/${id}/execute/`, data);
  }

  public toLayerModel(data: any): SnLayer {
    switch (data.type) {
      case SnLayerType.Internal:
        return new SnInternalLayer(data);

      case SnLayerType.External:
        return new SnExternalLayer(data);

      case SnLayerType.EsriStream:
        return new SnEsriStreamLayer(data);

      case SnLayerType.EsriQuery:
        return new SnEsriQueryLayer(data);

      case SnLayerType.GeoJSON:
        return new SnGeoJSONLayer(data);

      case SnLayerType.InternalGeoJSON:
        return new SnInternalGeoJSONLayer(data);

      case SnLayerType.SomaGlobal:
        return new SnEsriQueryLayer(data);

      case SnLayerType.Integration:
        switch (data.integrationType) {
          case 'FirstTwo':
            return new SnFirstTwoLayer(data);
          case 'Redbook':
            return new SnRedbookLayer(data);
          case 'AerisWeather':
            return new SnAerisWeatherLayer(data);
        }
        return new SnIntegrationLayer(data);

      default:
        console.error(`${data.type} layer isn't supported`);
        return null;
    }
  }

  public supportedLayers(data: any): boolean {
    if (data.meta?.permissions?.length > 0) {
      let canSee = false;
      const perms = data.meta?.permissions;
      perms.forEach((perm) => {
        if (this._ability.can({ subject: perm.subject, action: [perm.action] })) {
          canSee = true;
        }
      });
      if (!canSee) {
        return false;
      }
    }
    if (data.meta?.restricted) {
      if (this._ability.cannot({ subject: 'maps/layer', action: ['change'] })) {
        return false;
      }
    }
    return Object.values(SnLayerType).includes(data.type);
  }

  public getShareList(
    layerId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnShare>> {
    return this._httpClient.get(`/api/layers/${layerId}/shares/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((share) => new SnShare(share)),
      })),
    );
  }

  public createShare(layerId: string, data: any): Observable<SnShare | SnShare[]> {
    return this._httpClient.post(`/api/layers/${layerId}/shares/`, data).pipe(
      map((response: any) => {
        return Array.isArray(response)
          ? response.map((share) => new SnShare(share))
          : new SnShare(response);
      }),
    );
  }

  public updateShare(layerId: string, shareId: string, data: any): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/layers/${layerId}/shares/${shareId}/`, data)
      .pipe(map((response: any) => new SnShare(response)));
  }

  public deleteShare(layerId: string, shareId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'Are you sure you want to delete the share?',
        confirmButton: {
          text: 'DELETE',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.delete(`/api/layers/${layerId}/shares/${shareId}/`);
        }),
      );
  }
}
